import { apiClient } from '@/core/api/client';
import type {
  Task,
  TasksResponse,
  TaskStatsResponse,
  UsersResponse,
  TaskFormData,
  WaitingStatus,
  UrgencyLevel
} from '../types/task.types';

export const tasksAPI = {
  /**
   * Get all tasks (own, team, created)
   */
  async getAllTasks(): Promise<TasksResponse> {
    try {
      return apiClient.get<TasksResponse>('/api/admin/mes-taches');
    } catch (error) {
      console.error('Error fetching tasks:', error);
      throw error;
    }
  },

  /**
   * Get task statistics
   */
  async getStats(): Promise<TaskStatsResponse> {
    try {
      return apiClient.get<TaskStatsResponse>('/api/admin/mes-taches/stats');
    } catch (error) {
      console.error('Error fetching task stats:', error);
      throw error;
    }
  },

  /**
   * Get all users for task assignment
   */
  async getAllUsers(): Promise<UsersResponse> {
    try {
      return apiClient.get<UsersResponse>('/api/admin/mes-taches/users');
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  /**
   * Get task statistics for all users (admin only)
   */
  async getAllUsersTaskStats(): Promise<{
    success: boolean;
    data: Array<{
      id: string;
      email: string;
      first_name: string;
      last_name: string;
      role: string;
      stats: {
        active_tasks: number;
        active_own_tasks: number;
        active_team_tasks: number;
        active_created_tasks: number;
        unread_tasks: number;
        urgent_tasks: number;
      }
    }>
  }> {
    try {
      return apiClient.get<{
        success: boolean;
        data: Array<{
          id: string;
          email: string;
          first_name: string;
          last_name: string;
          role: string;
          stats: {
            active_tasks: number;
            active_own_tasks: number;
            active_team_tasks: number;
            active_created_tasks: number;
            unread_tasks: number;
            urgent_tasks: number;
          }
        }>
      }>('/api/admin/mes-taches/admin/users-stats');
    } catch (error) {
      console.error('Error fetching users task stats:', error);
      throw error;
    }
  },

  /**
   * Create a new task
   */
  async createTask(taskData: TaskFormData): Promise<{ success: boolean; data: Task }> {
    try {
      return apiClient.post<{ success: boolean; data: Task }>('/api/admin/mes-taches', taskData);
    } catch (error) {
      console.error('Error creating task:', error);
      throw error;
    }
  },

  /**
   * Update a task
   */
  async updateTask(taskId: string, taskData: Partial<TaskFormData>): Promise<{ success: boolean; data: Task }> {
    try {
      return apiClient.put<{ success: boolean; data: Task }>(`/api/admin/mes-taches/${taskId}`, taskData);
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  },

  /**
   * Mark task as done.
   * `options.k_id_affaire` (optionnel) lie l'affaire au moment de la
   * validation — utilisé par les tâches issues d'un message vocal, où le
   * backend attribue alors aussi le message vocal à l'affaire.
   */
  async markTaskDone(taskId: string, options?: { k_id_affaire?: number }): Promise<{ success: boolean; data: Task }> {
    try {
      return apiClient.put<{ success: boolean; data: Task }>(`/api/admin/mes-taches/${taskId}/done`, options);
    } catch (error) {
      console.error('Error marking task as done:', error);
      throw error;
    }
  },

  /**
   * Unmark task as done
   */
  async unmarkTaskDone(taskId: string): Promise<{ success: boolean; data: Task }> {
    try {
      return apiClient.put<{ success: boolean; data: Task }>(`/api/admin/mes-taches/${taskId}/undone`);
    } catch (error) {
      console.error('Error unmarking task as done:', error);
      throw error;
    }
  },

  /**
   * Update task waiting status
   */
  async updateTaskStatus(taskId: string, waitingStatus: WaitingStatus): Promise<{ success: boolean; data: Task }> {
    try {
      return apiClient.put<{ success: boolean; data: Task }>(`/api/admin/mes-taches/${taskId}/status`, {
        waiting_status: waitingStatus
      });
    } catch (error) {
      console.error('Error updating task status:', error);
      throw error;
    }
  },

  /**
   * Update task urgency/priority (criticity)
   */
  async updateTaskUrgency(taskId: string, urgency: UrgencyLevel): Promise<{ success: boolean; data: Task }> {
    try {
      return apiClient.put<{ success: boolean; data: Task }>(`/api/admin/mes-taches/${taskId}`, {
        criticity: urgency
      });
    } catch (error) {
      console.error('Error updating task urgency:', error);
      throw error;
    }
  },

  /**
   * Delete a task
   */
  async deleteTask(taskId: string): Promise<{ success: boolean }> {
    try {
      return apiClient.delete<{ success: boolean }>(`/api/admin/mes-taches/${taskId}`);
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  },

  /**
   * Set task as informative
   */
  async setTaskAsInformative(taskId: string): Promise<{ success: boolean; data: Task }> {
    try {
      return apiClient.put<{ success: boolean; data: Task }>(`/api/admin/mes-taches/${taskId}/set-informative`);
    } catch (error) {
      console.error('Error setting task as informative:', error);
      throw error;
    }
  },

  /**
   * Mark task as viewed (update last viewed timestamp)
   */
  async markTaskViewed(taskId: string): Promise<{ success: boolean }> {
    try {
      return apiClient.post<{ success: boolean }>(`/api/admin/mes-taches/${taskId}/viewed`);
    } catch (error) {
      console.error('Error marking task as viewed:', error);
      throw error;
    }
  },

  /**
   * Mark task as unread
   */
  async markTaskUnread(taskId: string): Promise<{ success: boolean }> {
    try {
      return apiClient.delete<{ success: boolean }>(`/api/admin/mes-taches/${taskId}/viewed`);
    } catch (error) {
      console.error('Error marking task as unread:', error);
      throw error;
    }
  },

  /**
   * Get total unread count for user
   */
  async getUnreadCount(): Promise<{ success: boolean; data: { unread_count: number } }> {
    try {
      return apiClient.get<{ success: boolean; data: { unread_count: number } }>('/api/admin/mes-taches/unread/count');
    } catch (error) {
      console.error('Error getting unread count:', error);
      throw error;
    }
  },

  /**
   * Get new tasks since a timestamp for notifications
   */
  async getNewTasksSince(timestamp: number): Promise<{ success: boolean; data: { new_tasks: Task[]; count: number } }> {
    try {
      return apiClient.get<{ success: boolean; data: { new_tasks: Task[]; count: number } }>(`/api/admin/mes-taches/new/since/${timestamp}`);
    } catch (error) {
      console.error('Error getting new tasks:', error);
      throw error;
    }
  },

  /**
   * Get tasks for a specific user (admin only) - New admin endpoint
   */
  async getTasksForUser(userId: string): Promise<TasksResponse> {
    try {
      return apiClient.get<TasksResponse>(`/api/admin/mes-taches/admin/user/${userId}`);
    } catch (error) {
      console.error('Error fetching tasks for user:', error);
      throw error;
    }
  },

  /**
   * Get all tasks in the system (admin only) - New admin endpoint
   */
  async getAllSystemTasks(): Promise<{ success: boolean; data: Task[] }> {
    try {
      return apiClient.get<{ success: boolean; data: Task[] }>('/api/admin/mes-taches/admin/all');
    } catch (error) {
      console.error('Error fetching all system tasks:', error);
      throw error;
    }
  },

  /**
   * Assign a task to users/roles (admin/bureau only)
   */
  async assignTask(taskId: string, assignmentData: {
    assignedToUsers?: string[];
    assignedToRoles?: string[];
  }): Promise<{ success: boolean; data: Task }> {
    try {
      return apiClient.put<{ success: boolean; data: Task }>(`/api/admin/mes-taches/${taskId}/assign`, assignmentData);
    } catch (error) {
      console.error('Error assigning task:', error);
      throw error;
    }
  }
};
